// Sign-up funnel events, sent to Google Analytics.
//
//   landing_view      a signed-out visitor saw a tool's landing view (/g25 …)
//   landing_cta       they pressed "Create free account" or "Sign in" on it
//   auth_wall         a gated route with no landing view raised the sign-in
//                     modal on arrival: the baseline the landing views are
//                     measured against
//   signup_completed  a new account signed in for the first time, tagged
//                     with the landing or wall the visitor last came through
//
// GA, not Datadog RUM: RUM only starts once someone is signed in (the
// monitoring component mounts on `user || contacted`), so every visit that
// did not convert, the denominator of the funnel, would never reach it. GA
// runs for everyone (vue-gtag-next in main.js). In development, where there
// may be no measurement id, the events also go to the console.
import { event as gtagEvent } from 'vue-gtag-next'

const SOURCE_KEY = 'genoSignupSource'
// Long enough to cover an email-code confirmation finished later the same
// day, short enough that a visit last month does not claim today's sign-up.
const SOURCE_TTL_MS = 24 * 60 * 60 * 1000

const isDev = () => typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1|0\.0\.0\.0|dev\.genoplot\.com)$/.test(window.location.hostname)

export function trackFunnel(name, params = {}) {
    if (isDev()) console.debug('[funnel]', name, params)
    try {
        gtagEvent(name, { event_category: 'funnel', ...params })
    } catch (err) {
        // No measurement id (gtag never installed): nothing to send to.
    }
}

// Where the visitor came from, kept until the account exists. localStorage,
// not sessionStorage: Google/Apple/Facebook sign-in leaves the app for the
// Cognito Hosted UI and comes back in a fresh document, and the sign-in
// flows clear sessionStorage on the way out.
export function rememberSignupSource(source) {
    try {
        localStorage.setItem(SOURCE_KEY, JSON.stringify({ ...source, at: Date.now() }))
    } catch (err) { /* private mode: attribution is lost, nothing else */ }
}

export function peekSignupSource() {
    try {
        const source = JSON.parse(localStorage.getItem(SOURCE_KEY) || 'null')
        if (!source || !source.at || Date.now() - source.at > SOURCE_TTL_MS) return null
        return source
    } catch (err) {
        return null
    }
}

export function consumeSignupSource() {
    const source = peekSignupSource()
    try { localStorage.removeItem(SOURCE_KEY) } catch (err) { /* nothing to clear */ }
    return source
}

// Once per account: a first sign-in can repeat (another tab, a reload before
// the terms are accepted), and the account should be counted once.
export function trackSignupCompleted(username, params = {}) {
    const flag = `genoSignupTracked:${username}`
    try {
        if (localStorage.getItem(flag)) return
        localStorage.setItem(flag, '1')
    } catch (err) { /* storage blocked: may double count, still report */ }
    const source = consumeSignupSource()
    trackFunnel('signup_completed', {
        ...params,
        source_kind: source ? source.kind : 'direct',
        source_route: source ? source.route : '',
        source_landing: (source && source.landing) || '',
        source_cta: (source && source.cta) || '',
    })
}
